import { type LngLat, type YMap as YMapType, type YMapLocationRequest } from '@yandex/ymaps3-types';
import { ref, shallowRef } from 'vue';

import { useYMaps } from './useYMaps';
import { useLocation } from './useLocation';

const RostovOnDonCoordinates: LngLat = [39.71724, 47.226893];

async function addUserMarker(map: YMapType) {
  const { YMapDefaultMarker } = await import('@yandex/ymaps3-default-ui-theme');

  useLocation()
    .getLocation()
    .then(location => {
      if (!location) {
        return;
      }

      const userMarker = new YMapDefaultMarker({
        coordinates: location
      });

      map.addChild(userMarker);
    });
}

export function useGeneralMap({
  location = undefined
}: {
  location?: YMapLocationRequest;
} = {}) {
  const { ymaps3Lib, waitForYMaps } = useYMaps();
  const isMapsUnavailable = ref(false);

  const map = shallowRef<YMapType | null>(null);

  async function initMap(mapEl: HTMLElement | null) {
    await Promise.all([waitForYMaps()]);

    if (!ymaps3Lib.value) {
      isMapsUnavailable.value = true;
      // eslint-disable-next-line no-console
      console.warn('ymaps3 not available');
      return;
    }

    const LOCATION: YMapLocationRequest = location || {
      center: location || RostovOnDonCoordinates,
      zoom: 12
    };

    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer } = ymaps3Lib.value;

    if (!mapEl || !YMap || map.value) {
      return;
    }

    try {
      map.value = new YMap(mapEl, {
        location: LOCATION,
        behaviors: ['drag', 'scrollZoom', 'pinchZoom', 'dblClick']
      });

      if (!map.value) {
        throw new Error('Map not initialized');
      }

      map.value.addChild(new YMapDefaultSchemeLayer({}));
      map.value.addChild(new YMapDefaultFeaturesLayer({}));

      addUserMarker(map.value);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize map:', error);
    }
  }

  return {
    map,
    initMap
  };
}
